import React, { useState, useEffect } from 'react';
import { Radio, Wifi, Thermometer, Lock, Users, Bell, Activity, Zap, GitMerge, Plus, X, Settings, Droplets, Wind, Database, Save, RefreshCw, Play } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface IntegratedSystem {
  id: string;
  name: string;
  type: string;
  manufacturer?: string;
  model?: string;
  location?: string;
  apiEndpoint?: string;
  apiKey?: string;
  updateInterval?: number;
  status: 'connected' | 'disconnected' | 'configuring';
  lastSync: Date;
  metrics: {
    name: string;
    value: number | string;
    unit: string;
    trend?: 'up' | 'down' | 'stable';
  }[];
}

interface CombinedInsight {
  id: string;
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  systems: string[];
  recommendation: string;
}

const SENSOR_TYPES = [
  { id: 'temp', name: 'Temperature Sensors', icon: Thermometer },
  { id: 'water', name: 'Water Sensors', icon: Droplets },
  { id: 'air', name: 'Air Quality Sensors', icon: Wind },
  { id: 'occupancy', name: 'Occupancy Sensors', icon: Users },
  { id: 'access', name: 'Access Control', icon: Lock },
  { id: 'energy', name: 'Energy Meters', icon: Zap },
];

const SensorIntegrationHub: React.FC = () => {
  // State for systems and insights
  const [systems, setSystems] = useState<IntegratedSystem[]>([
    {
      id: 'hvac-1',
      name: 'Building HVAC',
      type: 'climate',
      manufacturer: 'Johnson Controls',
      model: 'FEC-2500',
      location: 'Mechanical Room',
      status: 'connected',
      lastSync: new Date(),
      metrics: [
        { name: 'Temperature', value: 72.5, unit: '°F', trend: 'stable' },
        { name: 'Energy Usage', value: 450, unit: 'kWh', trend: 'down' }
      ]
    },
    {
      id: 'access-1',
      name: 'Access Control',
      type: 'security',
      manufacturer: 'HID Global',
      model: 'EdgeReader',
      location: 'All Entrances',
      status: 'connected',
      lastSync: new Date(),
      metrics: [
        { name: 'Active Cards', value: 342, unit: 'cards', trend: 'up' },
        { name: 'Door Events', value: 1205, unit: 'events', trend: 'up' }
      ]
    }
  ]);

  // State for UI controls
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [selectedSystem, setSelectedSystem] = useState<IntegratedSystem | null>(null);
  const [showConfigPanel, setShowConfigPanel] = useState(false);
  const [newSensorForm, setNewSensorForm] = useState({
    type: '',
    name: '',
    manufacturer: '',
    model: '',
    location: '',
    apiEndpoint: '',
    apiKey: '',
    updateInterval: 5
  });

  // Correlation data state
  const [correlationData, setCorrelationData] = useState<any[]>([]);
  const [isSimulating, setIsSimulating] = useState(true);

  // Effect for simulation data
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isSimulating) {
      interval = setInterval(() => {
        const now = new Date();
        const newDataPoint = {
          time: now.toLocaleTimeString(),
          floorSensorCount: Math.floor(Math.random() * 50) + 250,
          badgeScanCount: Math.floor(Math.random() * 50) + 300,
          occupancySensorCount: Math.floor(Math.random() * 50) + 270
        };
        setCorrelationData(prev => [...prev.slice(-20), newDataPoint]);
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [isSimulating]);

  // Handle new sensor submission
  const handleAddSensor = () => {
    const newSensor: IntegratedSystem = {
      id: `${newSensorForm.type}-${Date.now()}`,
      name: newSensorForm.name,
      type: newSensorForm.type,
      manufacturer: newSensorForm.manufacturer,
      model: newSensorForm.model,
      location: newSensorForm.location,
      apiEndpoint: newSensorForm.apiEndpoint,
      apiKey: newSensorForm.apiKey,
      status: 'configuring',
      lastSync: new Date(),
      metrics: []
    };
    
    setSystems(prev => [...prev, newSensor]);
    setShowOnboarding(false);
    setNewSensorForm({
      type: '',
      name: '',
      manufacturer: '',
      model: '',
      location: '',
      apiEndpoint: '',
      apiKey: '',
      updateInterval: 5
    });
  };

  // Handle system configuration updates
  const handleUpdateSystem = (system: IntegratedSystem) => {
    setSystems(prev => prev.map(s => s.id === system.id ? system : s));
    setSelectedSystem(null);
    setShowConfigPanel(false);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header with Controls */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold mb-2">Sensor Integration Hub</h2>
            <p className="text-gray-600">
              Centralize and enhance building intelligence through sensor integration
            </p>
          </div>
          <div className="flex space-x-4">
            <button
              onClick={() => setShowOnboarding(true)}
              className="flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              <Plus className="w-5 h-5" />
              <span>Add New Sensor</span>
            </button>
            <button
              onClick={() => setIsSimulating(!isSimulating)}
              className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
            >
              {isSimulating ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  <span>Simulating</span>
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  <span>Start Simulation</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Connected Systems Overview */}
      <div className="grid grid-cols-3 gap-6">
        {systems.map(system => (
          <div key={system.id} className="bg-white rounded-lg shadow p-4">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-semibold">{system.name}</h3>
                <span className={`text-sm px-2 py-1 rounded ${
                  system.status === 'connected' ? 'bg-green-100 text-green-800' :
                  system.status === 'configuring' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {system.status}
                </span>
              </div>
              <div className="flex space-x-2">
                <button 
                  onClick={() => {
                    setSelectedSystem(system);
                    setShowConfigPanel(true);
                  }}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <Settings className="w-5 h-5 text-gray-500" />
                </button>
                {system.type === 'climate' && <Thermometer className="w-6 h-6 text-blue-500" />}
                {system.type === 'water' && <Droplets className="w-6 h-6 text-blue-500" />}
                {system.type === 'security' && <Lock className="w-6 h-6 text-blue-500" />}
                {system.type === 'occupancy' && <Users className="w-6 h-6 text-blue-500" />}
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-3">
              <div>Manufacturer: {system.manufacturer}</div>
              <div>Model: {system.model}</div>
              <div>Location: {system.location}</div>
            </div>
            <div className="space-y-2">
              {system.metrics.map((metric, idx) => (
                <div key={idx} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{metric.name}</span>
                  <span className="font-medium">
                    {metric.value} {metric.unit}
                    {metric.trend === 'up' && <Activity className="w-4 h-4 inline ml-1 text-green-500" />}
                    {metric.trend === 'down' && <Activity className="w-4 h-4 inline ml-1 text-red-500" />}
                    {metric.trend === 'stable' && <Activity className="w-4 h-4 inline ml-1 text-blue-500" />}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Data Correlation Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4">Real-time Data Correlation</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={correlationData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="floorSensorCount" 
                stroke="#3b82f6" 
                name="Floor Sensor Count"
              />
              <Line 
                type="monotone" 
                dataKey="badgeScanCount" 
                stroke="#10b981" 
                name="Badge Scan Count"
              />
              <Line 
                type="monotone" 
                dataKey="occupancySensorCount" 
                stroke="#f59e0b" 
                name="Occupancy Sensor Count"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Onboarding Modal */}
      {showOnboarding && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-[600px]">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Add New Sensor</h3>
              <button
                onClick={() => setShowOnboarding(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Sensor Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sensor Type
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {SENSOR_TYPES.map(type => (
                    <button
                      key={type.id}
                      onClick={() => setNewSensorForm(prev => ({ ...prev, type: type.id }))}
                      className={`p-3 border rounded-lg flex flex-col items-center space-y-2 ${
                        newSensorForm.type === type.id ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <type.icon className="w-6 h-6 text-blue-500" />
                      <span className="text-sm">{type.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Sensor Details Form */}
              {newSensorForm.type && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sensor Name
                    </label>
                    <input
                      type="text"
                      value={newSensorForm.name}
                      onChange={e => setNewSensorForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-md"
                      placeholder="e.g., North Wing Temperature Sensor"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Manufacturer
                      </label>
                      <input
                        type="text"
                        value={newSensorForm.manufacturer}
                        onChange={e => setNewSensorForm(prev => ({ ...prev, manufacturer: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-md"
                        placeholder="e.g., Johnson Controls"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Model
                      </label>
                      <input
                        type="text"
                        value={newSensorForm.model}
                        onChange={e => setNewSensorForm(prev => ({ ...prev, model: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-md"
                        placeholder="e.g., TEC3000"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Location
                    </label>
                    <input
                      type="text"
                      value={newSensorForm.location}
                      onChange={e => setNewSensorForm(prev => ({ ...prev, location: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-md"
                      placeholder="e.g., North Wing, Floor 3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API Endpoint
                    </label>
                    <input
                      type="text"
                      value={newSensorForm.apiEndpoint}
                      onChange={e => setNewSensorForm(prev => ({ ...prev, apiEndpoint: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-md"
                      placeholder="e.g., https://api.example.com/v1/sensors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API Key
                    </label>
                    <input
                      type="password"
                      value={newSensorForm.apiKey}
                      onChange={e => setNewSensorForm(prev => ({ ...prev, apiKey: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-md"
                      placeholder="Enter API key"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Update Interval (minutes)
                    </label>
                    <input
                      type="number"
                      value={newSensorForm.updateInterval}
                      onChange={e => setNewSensorForm(prev => ({ ...prev, updateInterval: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border rounded-md"
                      min="1"
                      max="60"
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-2 mt-6">
                <button
                  onClick={() => setShowOnboarding(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddSensor}
                  disabled={!newSensorForm.type || !newSensorForm.name}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Add Sensor
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Configuration Panel */}
      {showConfigPanel && selectedSystem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-[500px]">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Configure Sensor</h3>
              <button
                onClick={() => {
                  setSelectedSystem(null);
                  setShowConfigPanel(false);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={selectedSystem.status}
                  onChange={e => setSelectedSystem({ ...selectedSystem, status: e.target.value as 'connected' | 'disconnected' | 'configuring' })}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="connected">Connected</option>
                  <option value="disconnected">Disconnected</option>
                  <option value="configuring">Configuring</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  API Endpoint
                </label>
                <input
                  type="text"
                  value={selectedSystem.apiEndpoint || ''}
                  onChange={e => setSelectedSystem({ ...selectedSystem, apiEndpoint: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  API Key
                </label>
                <input
                  type="password"
                  value={selectedSystem.apiKey || ''}
                  onChange={e => setSelectedSystem({ ...selectedSystem, apiKey: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={selectedSystem.location || ''}
                  onChange={e => setSelectedSystem({ ...selectedSystem, location: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <button
                  onClick={() => {
                    setSelectedSystem(null);
                    setShowConfigPanel(false);
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleUpdateSystem(selectedSystem)}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SensorIntegrationHub; 